import { useMutation } from '@apollo/client';
import Box from '@risk-smart/themed-cloudscape-components/box';
import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import Modal from '@risk-smart/themed-cloudscape-components/modal';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  RecalculateRiskScoresDocument,
  Risk_Scoring_Model_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledJsonEditor } from 'src/components/form/controlled-json-editor/ControlledJsonEditor';
import ControlledSelect from 'src/components/form/controlled-select';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

export const RiskScoreSettings: FC = () => {
  const { control } = useFormContext();
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { t: ct } = useTranslation('common');
  const isCustomerSupport = useIsCustomerSupport();
  const { addNotification } = useNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [recalculateRiskScores, { loading: recalculating }] = useMutation(
    RecalculateRiskScoresDocument
  );

  const handleRecalculate = async () => {
    setShowConfirmModal(false);
    try {
      await recalculateRiskScores();
      addNotification({
        type: 'success',
        content: t('RecalculateRiskScores_success'),
      });
    } catch {
      addNotification({
        type: 'error',
        content: t('RecalculateRiskScores_error'),
      });
    }
  };

  return (
    <SpaceBetween size={'s'}>
      <FormField>
        <ControlledSelect
          control={control}
          testId={'riskScoringModel'}
          name={'RiskScoringModel'}
          label={t('RiskScoringModel')}
          options={[
            {
              label: t('RiskScoringModels.default'),
              value: Risk_Scoring_Model_Enum.Default,
            },
            {
              label: t('RiskScoringModels.control_effectiveness_averages'),
              value: Risk_Scoring_Model_Enum.ControlEffectivenessAverages,
            },
            {
              label: t(
                'RiskScoringModels.typed_control_effectiveness_averages'
              ),
              value: Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages,
            },
          ]}
        />
      </FormField>
      <FormField>
        <ControlledJsonEditor
          control={control}
          name={'RiskScoringModelConfig'}
          label={t('RiskScoringModelConfig')}
        />
      </FormField>
      {isCustomerSupport && (
        <>
          <Button
            data-testid={'recalculate-risk-scores-button'}
            variant={'normal'}
            onClick={() => setShowConfirmModal(true)}
            loading={recalculating}
            disabled={recalculating}
          >
            {t('RecalculateRiskScores')}
          </Button>
          <Modal
            data-testid={'recalculate-risk-scores-modal'}
            visible={showConfirmModal}
            onDismiss={() => setShowConfirmModal(false)}
            header={t('RecalculateRiskScores_confirm_header')}
            closeAriaLabel={ct('closeModal')}
            size={'small'}
            footer={
              <Box float={'right'}>
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Button
                    variant={'normal'}
                    onClick={() => setShowConfirmModal(false)}
                  >
                    {ct('cancel')}
                  </Button>
                  <Button variant={'primary'} onClick={handleRecalculate}>
                    {t('RecalculateRiskScores_confirm_button')}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            {t('RecalculateRiskScores_confirm_body')}
          </Modal>
        </>
      )}
    </SpaceBetween>
  );
};
